/**
 * Hook to sync dev role override with LensesContext
 * 
 * This ensures that when you switch roles in the dev role switcher,
 * it automatically updates the preview role in templates/objects
 */

import { useEffect, useRef } from 'react';
import { useDevRoleOverride, isDevelopmentMode } from '@/lib/utils/devRoleOverride';
import { useLensesOptional } from '@/contexts/LensesContext';
import { useSimplifiedUserData } from '@/providers/SimplifiedUserDataProvider';

export function useDevRoleSync() {
  const { overrideRoleId } = useDevRoleOverride();
  const lensesContext = useLensesOptional();
  const { selectedCompany } = useSimplifiedUserData();
  const prevOverrideRoleId = useRef<string | null>(null);

  useEffect(() => {
    // Only sync if in development mode and we have a LensesContext
    if (!isDevelopmentMode() || !lensesContext) {
      return;
    }

    if (overrideRoleId) {
      // Dev role override is active - set it as the preview role and enable preview mode
      console.log('🎭 [DevRoleSync] ⚡ Role switched - activating preview mode:', overrideRoleId);
      lensesContext.setSelectedRole(overrideRoleId);
      lensesContext.setPreviewMode(true);
      prevOverrideRoleId.current = overrideRoleId;
      
      // Force component re-renders by updating preview mode state
      // This ensures all role-dependent logic recalculates
      console.log('🎭 [DevRoleSync] ✅ Preview mode active - components will re-check permissions');
    } else if (prevOverrideRoleId.current) {
      // Override was active before but now it's cleared - only then reset preview mode
      console.log('🎭 [DevRoleSync] 🔄 Override cleared - returning to actual role');
      lensesContext.setPreviewMode(false);
      lensesContext.setSelectedRole(null);
      prevOverrideRoleId.current = null;
      console.log('🎭 [DevRoleSync] ✅ Preview mode disabled - showing actual permissions');
    }
    // If prevOverrideRoleId.current is null and overrideRoleId is null, do nothing
    // This prevents clearing manual preview mode set by the Role Visibility panel
  }, [overrideRoleId, lensesContext, selectedCompany]);

  return { overrideRoleId };
}

